import { NotificationPayload, createWebSocketClient } from './client';
import { getDeviceId, getPlatform } from '../plugins/device';

import config from '@/config';
import { getJwt } from '../auth';
import { handleNotificationTap } from '../plugins/pushNotifications';
import logger from '@/services/logger';
import { showNotificationToast } from '@/utils/toast';
import { useStore } from '@/store';

let client: ReturnType<typeof createWebSocketClient> | null = null;

// The gateway validates the appName/environment pair against the apps
// registered for that environment, and only the in-house brand is registered
// in development. Deriving the name from the switched-in brand there would ask
// for a pair the gateway does not know, so development always identifies as CSL
const DEV_APP_NAME = 'gox-csl';

const getAppName = (): string =>
  config.env === 'development'
    ? DEV_APP_NAME
    : `gox-${config.app.companyDetails.ownerId.toLowerCase()}`;

export const initWebSocket = async () => {
  if (client) return client;

  const { services, app, env } = config;

  client = createWebSocketClient({
    url: services.websocketHost,
    appName: getAppName(),
    appVersion: app.version ?? '',
    platform: await getPlatform(),
    deviceId: await getDeviceId(),
    environment: env,
    getToken: getJwt,
    onMessage: async (msg) => {
      logger.debug('onMessage', msg);
      switch (msg.type) {
        case 'notification':
          await showWsNotificationToast(msg);
          break;

        default:
          logger.warn('WS: type ignored', { msg });
          break;
      }
    },
    onStatus: (status) => {
      logger.info(`Connection status: ${status}`);
      useStore().websocketStatus = status;
    },
  });

  return client;
};

export const getWebSocketClient = () => {
  if (!client) throw new Error('WebSocket not initialised');
  return client;
};

export const disposeWebSocket = (): void => {
  client?.dispose();
  client = null;
};

export const sendRegisterEndpoint = (
  recipientId: string,
  platform: string,
  endpointArn: string,
): void => {
  if (!client) {
    logger.warn('sendRegisterEndpoint: WebSocket not initialised');
    return;
  }
  client.registerEndpoint(recipientId, platform, endpointArn);
};

const showWsNotificationToast = async (
  payload: NotificationPayload,
): Promise<void> => {
  showNotificationToast(payload.title, payload.body, async () => {
    await handleNotificationTap(payload);
  });
};
